<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { getImage, getImageSize } from "@/tools/image";
import {
    addImage,
    getResizedSize,
    getImageData,
    getInitialPoints,
    getInitialTriangles,
    drawTriangles,
    clearCanvas,
} from "@/tools/canvas";
import { Edit, Size } from "@/types";
import { useMainStore } from "@/stores/main";
import { splitWorstTriangle, updateTriangles } from "@/tools/triangles";
import { Triangle } from "@/types/";

const store = useMainStore();

const original = ref<HTMLCanvasElement | null>(null);
const render = ref<HTMLCanvasElement | null>(null);
const triangle = ref<HTMLCanvasElement | null>(null);

const setCanvasses = (size: Size) => {
    if (render.value) {
        render.value.width = size.width;
        render.value.height = size.height;
        store.canvas.render.context = render.value.getContext("2d");
        store.canvas.render.size = size;
    }
    if (triangle.value) {
        triangle.value.width = size.width;
        triangle.value.height = size.height;
        store.canvas.triangle.context = triangle.value.getContext("2d");
        store.canvas.triangle.size = size;
    }
};

const intervalId = ref<number | null>(null);

const tick = () => {
    createTick();
};

const createTick = () => {
    const filtered = store.triangles.filter((t) => t.active);
    updateTriangles(
        store.canvas.triangle,
        filtered,
        store.imageData,
        store.correction
    );

    const edit: Edit = splitWorstTriangle(filtered);
    store.points.push(edit.newPoint);
    store.triangles.push(...edit.newTriangles);
    store.history.push(edit);
    store.currentEdit = store.history.length - 1;
    drawTriangles(store.canvas.render, filtered);
};

const start = () => {
    intervalId.value = setInterval(tick);
};

const stop = () => {
    if (intervalId.value) {
        clearInterval(intervalId.value);
        intervalId.value = null;
    }
};

const revertTo = (index: number) => {
    const newTriangles: Triangle[] = [];
    for (let i = index; i < store.history.length; i++) {
        const edit = store.history[i];
        newTriangles.push(...edit.newTriangles);
    }
    const activeTriangles = store.triangles.filter(
        (t) => !newTriangles.includes(t)
    );
    drawTriangles(store.canvas.render, activeTriangles);
    clearCanvas(store.canvas.triangle);
    drawTriangles(
        store.canvas.triangle,
        store.history[index].oldTriangles,
        [0, 0, 0]
    );
    store.currentEdit = index;
};

const hasImage = ref(false);
const file = ref(null);

const atUpload = async () => {
    hasImage.value = true;
    if (!file.value || !file.value.type.startsWith("image/")) {
        alert("Het bestand is geen afbeelding.");
        return;
    } else {
        const image = new Image();
        image.src = URL.createObjectURL(file.value);
        if (original.value) {
            const context = original.value.getContext("2d");
            if (context) {
                await init(context, image);
                start();
            }
        }
    }
};

const useFallback = async () => {
    hasImage.value = true;
    const image = new Image();
    image.src = "mick-jagger.png";
    if (original.value) {
        const context = original.value.getContext("2d");
        if (context) {
            await init(context, image);
            start();
        }
    }
};

const init = async (
    originalContext: CanvasRenderingContext2D,
    image: HTMLImageElement
) => {
    store.canvas.original.context = originalContext;
    const originalSize = (await getImageSize(image)) as Size;
    const size = getResizedSize(originalSize);
    store.canvas.original.size = size;
    setCanvasses(size);
    addImage(originalContext, image, size);
    store.imageData = getImageData(store.canvas.original);
    store.points = getInitialPoints(size);
    store.triangles = getInitialTriangles(store.points);
};

watch(
    () => store.currentEdit,
    (newVal) => {
        revertTo(newVal);
    }
);
</script>

<template>
    <div class="App">
        <div class="App__tools">
            <div class="App__buttons">
                <v-btn v-if="intervalId" @click="stop">stop</v-btn>
                <v-btn v-else @click="start">Start</v-btn>
            </div>
            <!--            <div class="slider">-->
            <!--                <v-slider-->
            <!--                    v-model="store.correction"-->
            <!--                    :max="1000"-->
            <!--                    :min="1"-->
            <!--                    :step="50"-->
            <!--                    thumb-label-->
            <!--                />-->
            <!--            </div>-->

            <div class="slider">
                <v-slider
                    v-if="store.history.length > 1"
                    v-model="store.currentEdit"
                    :max="store.history.length - 1"
                    :min="0"
                    :step="1"
                    thumb-label
                />
            </div>

            <span> {{ store.history.length }} splits </span>
        </div>

        <div class="App__main">
            <canvas ref="render" />
            <div class="App__help-canvas">
                <canvas id="original" ref="original" />
                <canvas id="triangle" ref="triangle" />
            </div>
        </div>

        <div class="cover" v-if="!hasImage">
            <div class="cover__content">
                <v-file-upload
                    browse-text="Upload image"
                    title="Drag and drop an image to start Triangler"
                    v-model="file"
                    @change="atUpload"
                    density="default"
                ></v-file-upload>

                Or just use an image of Mick Jagger:
                <img src="mick-jagger.png" alt="Mick Jagger" width="100" />
                <v-btn @click="useFallback">Use Mick Jagger</v-btn>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
.cover {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;

    &__content {
        background: white;
        padding: 20px;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        gap: 20px;
        align-items: center;
    }
}

.App {
    padding: 20px;

    &__tools {
        display: flex;
        align-items: center;
        gap: 12px;

        .slider {
            width: 200px;

            .v-input__details {
                display: none;
            }
        }
    }

    &__buttons {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 200px;
    }

    &__main {
        padding: 20px;
        display: flex;
        gap: 10px;
        align-items: flex-start;
    }

    &__help-canvas {
        display: flex;
        flex-direction: column;
        gap: 10px;
        align-items: flex-start;
    }
}
canvas {
    background: #ddd;
    display: block;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
}

#original,
#triangle {
    transform: scale(0.5);
}
</style>
